import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ICON_SOCIAL_LOGIN_FACEBOOK } from 'assets/icons';
import { getEnvConstant } from 'constants/env';
import { instance } from 'utils/axiosInstance';
import { SocialLinkProviderType } from 'graphQL/Auth/types';
import { STORAGE_KEY } from 'utils/localStoarge';
import { useMutationCreateHost } from '../graphQL/hooks/useMutationCreateHost';
import { UserLoginResponse } from '../graphQL/types';
import { useGetMe } from 'graphQL/Auth/user/useGetMe';
import { SALE_LIST_PATH } from 'constants/path';
import HackleManager from 'utils/hackle';
import { EMAIL_REGEX } from 'utils/regExp';
import HostEmailModal from 'containers/Signup/components/HostEmailModal';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 12px 24px;
  width: 100%;

  border-radius: 8px;
  border: 1px solid #46474c;
  background: #fff;
  cursor: pointer;

  span {
    color: #46474c;

    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px; /* 142.857% */
    letter-spacing: -0.6px;
  }
`;

const SocialLogo = styled.img`
  position: absolute;
  left: 12px;
`;

interface FaceBookSDKIProps {
  handleIsShowSignupForm: () => void;
  handleSetLoginInfo: (info: UserLoginResponse) => void;
}

const FaceBookSDK: React.FC<FaceBookSDKIProps> = props => {
  const { handleIsShowSignupForm, handleSetLoginInfo } = props;
  const navigate = useNavigate();

  const { handleCreateHost } = useMutationCreateHost();
  const { handleRefetchUser } = useGetMe();
  const [pendingSignup, setPendingSignup] = useState<{
    errorData: any;
    access_token: string;
  }>();
  const [isFacebookSDKLoaded, setIsFacebookSDKLoaded] =
    useState<boolean>(false);

  const initializeFacebook = useCallback(() => {
    window.FB.init({
      appId: getEnvConstant('facebookAppKey'),
      xfbml: true,
      cookie: true,
      version: 'v8.0',
    });

    setIsFacebookSDKLoaded(true);
  }, [isFacebookSDKLoaded]);

  const handleSignIn = async (access_token: string) => {
    try {
      const response = await instance.post('/link/signin', {
        authorityLevel: 'HOST',
        provider: SocialLinkProviderType.Facebook,
        token: access_token,
        store: 'frip',
      });

      if (response) {
        localStorage.setItem(
          STORAGE_KEY.AUTHORIZATION,
          `${response.data.data.tokenType} ${response.data.data.accessToken}`,
        );

        handleRefetchUser(myInfo => {
          HackleManager.setUserId(
            myInfo.me.host.id,
            myInfo.me.host.name,
            myInfo.me.host.super,
          );
          HackleManager.logSignInComplete();

          navigate(SALE_LIST_PATH);
        });
      }
    } catch (error: any) {
      const errorCode = error.response.status;
      const errorData = error.response.data;

      if (errorCode === 403) {
        handleSignup(errorData, access_token);
      }

      if (errorCode === 401) {
        const response = await instance.post('/link/signin', {
          authorityLevel: 'USER',
          provider: SocialLinkProviderType.Facebook,
          token: access_token,
          store: 'frip',
        });

        if (response) {
          localStorage.setItem(
            STORAGE_KEY.AUTHORIZATION,
            `${response.data.data.tokenType} ${response.data.data.accessToken}`,
          );

          handleRefetchUser(() => {
            handleIsShowSignupForm();
          });
          handleSetLoginInfo(response.data.data);
        }
      }
    }
  };

  const handleSignup = async (
    errorData: any,
    access_token: string,
    inputEmail?: string,
  ) => {
    try {
      const {
        marketing,
        nickname,
        mobileNumber,
        validEmail,
        validMobileNumber,
      } = errorData.data;
      const email: string = inputEmail ?? errorData.data.email;

      if (!validMobileNumber) {
        alert('이미 가입된 번호입니다.');

        return;
      }

      // 소셜 계정에 올바른 이메일이 없으면 가입 전에 이메일을 입력받는다
      if (!EMAIL_REGEX.test(email ?? '')) {
        setPendingSignup({ errorData, access_token });

        return;
      }

      if (!inputEmail && !validEmail) {
        alert('이미 가입된 이메일입니다.');

        return;
      }

      const response = await instance.post('/link/signup', {
        authorityLevel: 'HOST',
        provider: SocialLinkProviderType.Facebook,
        token: access_token,
        store: 'frip',
        // 회원 가입은 기존과 동일하게 소셜 계정 이메일을 보내고, 입력받은 이메일은 호스트 생성에만 사용한다
        email: errorData.data.email,
        nickname,
        mobileNumber,
        agreement: {
          marketing,
        },
      });

      if (response) {
        const userResponse = await instance.post('/link/signin', {
          authorityLevel: 'USER',
          provider: SocialLinkProviderType.Facebook,
          token: access_token,
          store: 'frip',
        });

        HackleManager.logSignupVerificationComplete('facebook');

        localStorage.setItem(
          STORAGE_KEY.AUTHORIZATION,
          `${response.data.data.tokenType} ${response.data.data.accessToken}`,
        );

        handleCreateHost(
          { ...userResponse.data.data, email },
          mobileNumber,
          myInfo => {
            HackleManager.setUserId(myInfo.id, myInfo.name, myInfo.super);

            HackleManager.logSignupComplete('facebook', marketing);
          },
        );
      }
    } catch (error) {
      console.log(error, 'signup error');
    }
  };

  const handleOnClick = useCallback(() => {
    if (!isFacebookSDKLoaded) {
      return;
    }

    HackleManager.logSigninClick('facebook');

    window.FB.getLoginStatus((res: any) => {
      if (res.status === 'connected') {
        handleSignIn(res.authResponse.accessToken);
      } else {
        window.FB.login(
          (res: any) => {
            if (res.status === 'connected') {
              handleSignIn(res.authResponse.accessToken);
            } else {
              // raiseErrorToastAlert();
            }
          },
          {
            scope: 'email',
          },
        );
      }
    });
  }, [isFacebookSDKLoaded]);

  useEffect(() => {
    initializeFacebook();
  }, []);

  return (
    <React.Fragment>
      <Container onClick={handleOnClick}>
        <SocialLogo src={ICON_SOCIAL_LOGIN_FACEBOOK} alt="facebook logo" />{' '}
        <span>페이스북 계정으로 시작하기</span>
      </Container>
      {pendingSignup && (
        <HostEmailModal
          onClose={() => setPendingSignup(undefined)}
          onSubmit={email => {
            setPendingSignup(undefined);
            handleSignup(
              pendingSignup.errorData,
              pendingSignup.access_token,
              email,
            );
          }}
        />
      )}
    </React.Fragment>
  );
};

export default FaceBookSDK;
